import type { CharacterClass } from '@character-creator/types';
import { z } from 'zod';
import { ClassSchema } from '../schemas';

/**
 * SRD 5.1 class data
 * All data is validated against ClassSchema at module load time
 */
const rawClasses: CharacterClass[] = [
  {
    id: 'barbarian',
    name: 'Barbarian',
    description:
      'A fierce warrior who can enter a battle rage, the barbarian is defined by primal instincts and raw physical power.',
    hitDie: 'd12',
    primaryAbility: ['strength'],
    savingThrows: ['strength', 'constitution'],
    armorProficiencies: ['light', 'medium', 'shield'],
    weaponProficiencies: ['simple', 'martial'],
    toolProficiencies: [],
    skillChoices: {
      count: 2,
      from: [
        'animal-handling',
        'athletics',
        'intimidation',
        'nature',
        'perception',
        'survival',
      ],
    },
    startingEquipment: [
      {
        choose: 1,
        from: [['greataxe'], ['any-martial-melee']],
      },
      {
        choose: 1,
        from: [['handaxe', 'handaxe'], ['any-simple']],
      },
      'explorer-pack',
      'javelin',
      'javelin',
      'javelin',
      'javelin',
    ],
    features: [
      {
        id: 'rage',
        name: 'Rage',
        level: 1,
        description:
          'In battle, you fight with primal ferocity. On your turn, you can enter a rage as a bonus action. While raging, you gain advantage on Strength checks and saving throws, bonus rage damage, and resistance to bludgeoning, piercing, and slashing damage.',
      },
      {
        id: 'unarmored-defense-barbarian',
        name: 'Unarmored Defense',
        level: 1,
        description:
          "While you are not wearing any armor, your Armor Class equals 10 + your Dexterity modifier + your Constitution modifier. You can use a shield and still gain this benefit.",
      },
      {
        id: 'reckless-attack',
        name: 'Reckless Attack',
        level: 2,
        description:
          'When you make your first attack on your turn, you can decide to attack recklessly. Doing so gives you advantage on melee weapon attack rolls using Strength during this turn, but attack rolls against you have advantage until your next turn.',
      },
      {
        id: 'danger-sense',
        name: 'Danger Sense',
        level: 2,
        description:
          "You have advantage on Dexterity saving throws against effects that you can see, such as traps and spells. You can't be blinded, deafened, or incapacitated to gain this benefit.",
      },
      {
        id: 'primal-path',
        name: 'Primal Path',
        level: 3,
        description:
          'You choose a path that shapes the nature of your rage. Your choice grants you features at 3rd level and again at 6th, 10th, and 14th levels.',
      },
      {
        id: 'asi-4',
        name: 'Ability Score Improvement',
        level: 4,
        description:
          'You can increase one ability score by 2, or you can increase two ability scores by 1. You cannot increase an ability score above 20.',
      },
      {
        id: 'extra-attack-barbarian',
        name: 'Extra Attack',
        level: 5,
        description:
          'You can attack twice, instead of once, whenever you take the Attack action on your turn.',
      },
      {
        id: 'fast-movement',
        name: 'Fast Movement',
        level: 5,
        description:
          'Your speed increases by 10 feet while you are not wearing heavy armor.',
      },
      {
        id: 'feral-instinct',
        name: 'Feral Instinct',
        level: 7,
        description:
          "You have advantage on initiative rolls. Additionally, if you are surprised at the beginning of combat and aren't incapacitated, you can act normally on your first turn if you enter your rage before doing anything else.",
      },
      {
        id: 'asi-8',
        name: 'Ability Score Improvement',
        level: 8,
        description:
          'You can increase one ability score by 2, or you can increase two ability scores by 1.',
      },
      {
        id: 'brutal-critical-1',
        name: 'Brutal Critical',
        level: 9,
        description:
          "You can roll one additional weapon damage die when determining the extra damage for a critical hit with a melee attack.",
      },
      {
        id: 'asi-12',
        name: 'Ability Score Improvement',
        level: 12,
        description:
          'You can increase one ability score by 2, or you can increase two ability scores by 1.',
      },
      {
        id: 'brutal-critical-2',
        name: 'Brutal Critical',
        level: 13,
        description:
          'You can roll two additional weapon damage dice when determining the extra damage for a critical hit.',
      },
      {
        id: 'persistent-rage',
        name: 'Persistent Rage',
        level: 15,
        description:
          "Your rage is so fierce that it ends early only if you fall unconscious or if you choose to end it.",
      },
      {
        id: 'asi-16',
        name: 'Ability Score Improvement',
        level: 16,
        description:
          'You can increase one ability score by 2, or you can increase two ability scores by 1.',
      },
      {
        id: 'brutal-critical-3',
        name: 'Brutal Critical',
        level: 17,
        description:
          'You can roll three additional weapon damage dice when determining the extra damage for a critical hit.',
      },
      {
        id: 'indomitable-might',
        name: 'Indomitable Might',
        level: 18,
        description:
          'If your total for a Strength check is less than your Strength score, you can use that score in place of the total.',
      },
      {
        id: 'asi-19',
        name: 'Ability Score Improvement',
        level: 19,
        description:
          'You can increase one ability score by 2, or you can increase two ability scores by 1.',
      },
      {
        id: 'primal-champion',
        name: 'Primal Champion',
        level: 20,
        description:
          'Your Strength and Constitution scores increase by 4. Your maximum for those scores is now 24.',
      },
    ],
    subclassLevel: 3,
    subclassName: 'Primal Path',
    subclasses: [
      {
        id: 'berserker',
        name: 'Path of the Berserker',
        description:
          'For some barbarians, rage is a means to an end—that end being violence. The Path of the Berserker is a path of untrammeled fury.',
        features: [
          {
            id: 'frenzy',
            name: 'Frenzy',
            level: 3,
            description:
              'You can go into a frenzy when you rage. If you do so, for the duration of your rage you can make a single melee weapon attack as a bonus action on each of your turns after this one. When your rage ends, you suffer one level of exhaustion.',
          },
          {
            id: 'mindless-rage',
            name: 'Mindless Rage',
            level: 6,
            description:
              "You can't be charmed or frightened while raging. If you are charmed or frightened when you enter your rage, the effect is suspended for the duration.",
          },
          {
            id: 'intimidating-presence',
            name: 'Intimidating Presence',
            level: 10,
            description:
              'You can use your action to frighten someone with your menacing presence.',
          },
          {
            id: 'retaliation',
            name: 'Retaliation',
            level: 14,
            description:
              'When you take damage from a creature within 5 feet of you, you can use your reaction to make a melee weapon attack against that creature.',
          },
        ],
        source: 'srd-5.1',
      },
      {
        id: 'ignus-berserker',
        name: 'Path of the Berserker',
        description:
          "Ignus version: For some barbarians, rage is a means to an end—that end being violence. The Path of the Berserker is a path of untrammeled fury, slick with blood.",
        features: [],
        source: 'ignus',
      },
      {
        id: 'firewalker',
        name: 'Firewalker',
        description:
          "Fire is one of the most destructive forces in nature but it's also a force for renewal. Wildfire barbarians channel the power of flame to clear the path for their allies and scour the world of their foes.",
        features: [],
        source: 'ignus',
      },
      {
        id: 'nightstalker',
        name: 'Nightstalker',
        description:
          "The Nightstalker barbarian channels the primal instincts of the nocturnal predator. They use their rage to heighten their senses and sharpen their focus in pursuit of their prey.",
        features: [],
        source: 'ignus',
      },
    ],
    multiclassRequirements: [{ ability: 'strength', minimum: 13 }],
    multiclassProficiencies: {
      armor: ['shield'],
      weapons: ['simple', 'martial'],
    },
    source: 'srd-5.1',
  },
  {
    id: 'cleric',
    name: 'Cleric',
    description:
      'A priestly champion who wields divine magic in service of a higher power, clerics are intermediaries between the mortal world and the distant planes of the gods.',
    hitDie: 'd8',
    primaryAbility: ['wisdom'],
    savingThrows: ['wisdom', 'charisma'],
    armorProficiencies: ['light', 'medium', 'shield'],
    weaponProficiencies: ['simple'],
    toolProficiencies: [],
    skillChoices: {
      count: 2,
      from: ['history', 'insight', 'medicine', 'persuasion', 'religion'],
    },
    startingEquipment: [
      {
        choose: 1,
        from: [['mace'], ['warhammer']],
      },
      {
        choose: 1,
        from: [['scale-mail'], ['leather-armor'], ['chain-mail']],
      },
      {
        choose: 1,
        from: [['light-crossbow', 'crossbow-bolts-20'], ['any-simple']],
      },
      {
        choose: 1,
        from: [['priest-pack'], ['explorer-pack']],
      },
      'shield',
      'holy-symbol',
    ],
    features: [
      {
        id: 'spellcasting-cleric',
        name: 'Spellcasting',
        level: 1,
        description:
          'As a conduit for divine power, you can cast cleric spells. Wisdom is your spellcasting ability.',
      },
      {
        id: 'divine-domain',
        name: 'Divine Domain',
        level: 1,
        description:
          'Choose one domain related to your deity. Your choice grants you domain spells and other features at 1st level.',
      },
      {
        id: 'channel-divinity',
        name: 'Channel Divinity',
        level: 2,
        description:
          'You gain the ability to channel divine energy directly from your deity, using that energy to fuel magical effects.',
      },
      {
        id: 'asi-4',
        name: 'Ability Score Improvement',
        level: 4,
        description:
          'You can increase one ability score by 2, or you can increase two ability scores by 1.',
      },
      {
        id: 'destroy-undead-1',
        name: 'Destroy Undead',
        level: 5,
        description:
          'When an undead fails its saving throw against your Turn Undead, the creature is instantly destroyed if its CR is 1/2 or lower.',
      },
      {
        id: 'asi-8',
        name: 'Ability Score Improvement',
        level: 8,
        description:
          'You can increase one ability score by 2, or you can increase two ability scores by 1.',
      },
      {
        id: 'destroy-undead-2',
        name: 'Destroy Undead',
        level: 8,
        description: 'Destroys undead of CR 1 or lower.',
      },
      {
        id: 'divine-intervention',
        name: 'Divine Intervention',
        level: 10,
        description:
          'You can call on your deity to intervene on your behalf when your need is great.',
      },
      {
        id: 'destroy-undead-3',
        name: 'Destroy Undead',
        level: 11,
        description: 'Destroys undead of CR 2 or lower.',
      },
      {
        id: 'asi-12',
        name: 'Ability Score Improvement',
        level: 12,
        description:
          'You can increase one ability score by 2, or you can increase two ability scores by 1.',
      },
      {
        id: 'destroy-undead-4',
        name: 'Destroy Undead',
        level: 14,
        description: 'Destroys undead of CR 3 or lower.',
      },
      {
        id: 'asi-16',
        name: 'Ability Score Improvement',
        level: 16,
        description:
          'You can increase one ability score by 2, or you can increase two ability scores by 1.',
      },
      {
        id: 'destroy-undead-5',
        name: 'Destroy Undead',
        level: 17,
        description: 'Destroys undead of CR 4 or lower.',
      },
      {
        id: 'asi-19',
        name: 'Ability Score Improvement',
        level: 19,
        description:
          'You can increase one ability score by 2, or you can increase two ability scores by 1.',
      },
      {
        id: 'divine-intervention-improved',
        name: 'Divine Intervention Improvement',
        level: 20,
        description: 'Your call for intervention succeeds automatically.',
      },
    ],
    spellcasting: {
      type: 'full',
      ability: 'wisdom',
      ritual: true,
      focus: 'holy symbol',
      cantripsKnown: [3, 3, 3, 4, 4, 4, 4, 4, 4, 5, 5, 5, 5, 5, 5, 5, 5, 5, 5, 5],
      spellSlots: [
        [2, 0, 0, 0, 0, 0, 0, 0, 0],
        [3, 0, 0, 0, 0, 0, 0, 0, 0],
        [4, 2, 0, 0, 0, 0, 0, 0, 0],
        [4, 3, 0, 0, 0, 0, 0, 0, 0],
        [4, 3, 2, 0, 0, 0, 0, 0, 0],
        [4, 3, 3, 0, 0, 0, 0, 0, 0],
        [4, 3, 3, 1, 0, 0, 0, 0, 0],
        [4, 3, 3, 2, 0, 0, 0, 0, 0],
        [4, 3, 3, 3, 1, 0, 0, 0, 0],
        [4, 3, 3, 3, 2, 0, 0, 0, 0],
        [4, 3, 3, 3, 2, 1, 0, 0, 0],
        [4, 3, 3, 3, 2, 1, 0, 0, 0],
        [4, 3, 3, 3, 2, 1, 1, 0, 0],
        [4, 3, 3, 3, 2, 1, 1, 0, 0],
        [4, 3, 3, 3, 2, 1, 1, 1, 0],
        [4, 3, 3, 3, 2, 1, 1, 1, 0],
        [4, 3, 3, 3, 2, 1, 1, 1, 1],
        [4, 3, 3, 3, 3, 1, 1, 1, 1],
        [4, 3, 3, 3, 3, 2, 1, 1, 1],
        [4, 3, 3, 3, 3, 2, 2, 1, 1],
      ],
    },
    subclassLevel: 1,
    subclassName: 'Divine Domain',
    subclasses: [
      {
        id: 'life-domain',
        name: 'Life Domain',
        description:
          'The Life domain focuses on the vibrant positive energy that sustains all life. Gods of life promote vitality and health through healing the sick and wounded.',
        features: [
          {
            id: 'bonus-proficiency-life',
            name: 'Bonus Proficiency',
            level: 1,
            description: 'You gain proficiency with heavy armor.',
          },
          {
            id: 'disciple-of-life',
            name: 'Disciple of Life',
            level: 1,
            description:
              'Your healing spells are more effective. Whenever you use a spell of 1st level or higher to restore hit points, the creature regains additional hit points equal to 2 + the spell\'s level.',
          },
          {
            id: 'channel-divinity-preserve-life',
            name: 'Channel Divinity: Preserve Life',
            level: 2,
            description:
              'You can use Channel Divinity to heal the badly injured. You present your holy symbol and evoke healing energy that restores a number of hit points equal to five times your cleric level.',
          },
          {
            id: 'blessed-healer',
            name: 'Blessed Healer',
            level: 6,
            description:
              'When you cast a spell that restores hit points to another creature, you also regain hit points equal to 2 + the spell\'s level.',
          },
          {
            id: 'divine-strike-life',
            name: 'Divine Strike',
            level: 8,
            description:
              'You gain the ability to infuse your weapon strikes with divine energy. Once on each of your turns, you can deal an extra 1d8 radiant damage.',
          },
          {
            id: 'supreme-healing',
            name: 'Supreme Healing',
            level: 17,
            description:
              'When you roll dice to restore hit points with a spell, you can use the maximum number for each die instead of rolling.',
          },
        ],
        source: 'srd-5.1',
      },
      {
        id: 'ignus-life',
        name: 'Life Domain',
        description:
          "Ignus version (Shepherds): The Life domain focuses on the vibrant positive energy that sustains all life.",
        features: [],
        source: 'ignus',
      },
      {
        id: 'power',
        name: 'Power Domain',
        description:
          "Hand of the Titans: The Power domain focuses on creating, consolidating, defending power.",
        features: [],
        source: 'ignus',
      },
      {
        id: 'truth',
        name: 'Truth Domain',
        description:
          "Questioners: The Truth domain is devoted to rooting out the truth by whatever means necessary.",
        features: [],
        source: 'ignus',
      },
    ],
    multiclassRequirements: [{ ability: 'wisdom', minimum: 13 }],
    multiclassProficiencies: {
      armor: ['light', 'medium', 'shield'],
      weapons: [],
    },
    source: 'srd-5.1',
  },
  {
    id: 'fighter',
    name: 'Fighter',
    description:
      'A master of martial combat, skilled with a variety of weapons and armor. Fighters are versatile combatants who can specialize in various fighting styles.',
    hitDie: 'd10',
    primaryAbility: ['strength', 'dexterity'],
    savingThrows: ['strength', 'constitution'],
    armorProficiencies: ['light', 'medium', 'heavy', 'shield'],
    weaponProficiencies: ['simple', 'martial'],
    toolProficiencies: [],
    skillChoices: {
      count: 2,
      from: [
        'acrobatics',
        'animal-handling',
        'athletics',
        'history',
        'insight',
        'intimidation',
        'perception',
        'survival',
      ],
    },
    startingEquipment: [
      {
        choose: 1,
        from: [['chain-mail'], ['leather-armor', 'longbow', 'arrows-20']],
      },
      {
        choose: 1,
        from: [['any-martial', 'shield'], ['any-martial', 'any-martial']],
      },
      {
        choose: 1,
        from: [['light-crossbow', 'crossbow-bolts-20'], ['handaxe', 'handaxe']],
      },
      {
        choose: 1,
        from: [['dungeoneer-pack'], ['explorer-pack']],
      },
    ],
    features: [
      {
        id: 'fighting-style-fighter',
        name: 'Fighting Style',
        level: 1,
        description:
          'You adopt a particular style of fighting as your specialty.',
        choices: [
          {
            type: 'fighting-style',
            count: 1,
            from: [
              'archery',
              'defense',
              'dueling',
              'great-weapon-fighting',
              'protection',
              'two-weapon-fighting',
            ],
          },
        ],
      },
      {
        id: 'second-wind',
        name: 'Second Wind',
        level: 1,
        description:
          'You have a limited well of stamina that you can draw on to protect yourself from harm. On your turn, you can use a bonus action to regain hit points equal to 1d10 + your fighter level.',
      },
      {
        id: 'action-surge',
        name: 'Action Surge',
        level: 2,
        description:
          'You can push yourself beyond your normal limits for a moment. On your turn, you can take one additional action.',
      },
      {
        id: 'martial-archetype',
        name: 'Martial Archetype',
        level: 3,
        description:
          'You choose an archetype that you strive to emulate in your combat styles and techniques.',
      },
      {
        id: 'asi-4',
        name: 'Ability Score Improvement',
        level: 4,
        description:
          'You can increase one ability score by 2, or you can increase two ability scores by 1.',
      },
      {
        id: 'extra-attack-fighter',
        name: 'Extra Attack',
        level: 5,
        description:
          'You can attack twice, instead of once, whenever you take the Attack action on your turn.',
      },
      {
        id: 'asi-6',
        name: 'Ability Score Improvement',
        level: 6,
        description:
          'You can increase one ability score by 2, or you can increase two ability scores by 1.',
      },
      {
        id: 'asi-8',
        name: 'Ability Score Improvement',
        level: 8,
        description:
          'You can increase one ability score by 2, or you can increase two ability scores by 1.',
      },
      {
        id: 'indomitable',
        name: 'Indomitable',
        level: 9,
        description:
          'You can reroll a saving throw that you fail. If you do so, you must use the new roll.',
      },
      {
        id: 'extra-attack-2',
        name: 'Extra Attack',
        level: 11,
        description: 'You can attack three times whenever you take the Attack action.',
      },
      {
        id: 'asi-12',
        name: 'Ability Score Improvement',
        level: 12,
        description:
          'You can increase one ability score by 2, or you can increase two ability scores by 1.',
      },
      {
        id: 'indomitable-2',
        name: 'Indomitable',
        level: 13,
        description: 'You can use Indomitable twice between long rests.',
      },
      {
        id: 'asi-14',
        name: 'Ability Score Improvement',
        level: 14,
        description:
          'You can increase one ability score by 2, or you can increase two ability scores by 1.',
      },
      {
        id: 'asi-16',
        name: 'Ability Score Improvement',
        level: 16,
        description:
          'You can increase one ability score by 2, or you can increase two ability scores by 1.',
      },
      {
        id: 'action-surge-2',
        name: 'Action Surge',
        level: 17,
        description: 'You can use Action Surge twice before a rest.',
      },
      {
        id: 'indomitable-3',
        name: 'Indomitable',
        level: 17,
        description: 'You can use Indomitable three times between long rests.',
      },
      {
        id: 'asi-19',
        name: 'Ability Score Improvement',
        level: 19,
        description:
          'You can increase one ability score by 2, or you can increase two ability scores by 1.',
      },
      {
        id: 'extra-attack-3',
        name: 'Extra Attack',
        level: 20,
        description: 'You can attack four times whenever you take the Attack action.',
      },
    ],
    subclassLevel: 3,
    subclassName: 'Martial Archetype',
    subclasses: [
      {
        id: 'champion',
        name: 'Champion',
        description:
          'The archetypal Champion focuses on the development of raw physical power honed to deadly perfection.',
        features: [
          {
            id: 'improved-critical',
            name: 'Improved Critical',
            level: 3,
            description:
              'Your weapon attacks score a critical hit on a roll of 19 or 20.',
          },
          {
            id: 'remarkable-athlete',
            name: 'Remarkable Athlete',
            level: 7,
            description:
              'You can add half your proficiency bonus to any Strength, Dexterity, or Constitution check you make that doesn\'t already use your proficiency bonus.',
          },
          {
            id: 'additional-fighting-style',
            name: 'Additional Fighting Style',
            level: 10,
            description: 'You can choose a second option from the Fighting Style feature.',
          },
          {
            id: 'superior-critical',
            name: 'Superior Critical',
            level: 15,
            description: 'Your weapon attacks score a critical hit on a roll of 18-20.',
          },
          {
            id: 'survivor',
            name: 'Survivor',
            level: 18,
            description:
              'At the start of each of your turns, you regain hit points equal to 5 + your Constitution modifier if you have no more than half of your hit points left.',
          },
        ],
        source: 'srd-5.1',
      },
      {
        id: 'ignus-champion',
        name: 'Champion',
        description:
          "Ignus version: The archetypal Champion focuses on the development of raw physical power honed to deadly perfection.",
        features: [],
        source: 'ignus',
      },
      {
        id: 'pit-fighter',
        name: 'Pit Fighter',
        description:
          "Pit Fighters are the rough and tumble brawlers of Ignus. They understand that there are no rules to combat.",
        features: [],
        source: 'ignus',
      },
      {
        id: 'infiltrator',
        name: 'Infiltrator',
        description:
          "Masters of stealth and subterfuge, Infiltrators excel at covert operations.",
        features: [],
        source: 'ignus',
      },
    ],
    multiclassRequirements: [
      { ability: 'strength', minimum: 13 },
      { ability: 'dexterity', minimum: 13 },
    ],
    multiclassRequirementsLogic: 'or',
    multiclassProficiencies: {
      armor: ['light', 'medium', 'shield'],
      weapons: ['simple', 'martial'],
    },
    source: 'srd-5.1',
  },
  {
    id: 'rogue',
    name: 'Rogue',
    description:
      'A scoundrel who uses stealth and trickery to overcome obstacles and enemies. Rogues rely on skill, stealth, and their foe\'s vulnerabilities.',
    hitDie: 'd8',
    primaryAbility: ['dexterity'],
    savingThrows: ['dexterity', 'intelligence'],
    armorProficiencies: ['light'],
    weaponProficiencies: ['simple', 'hand-crossbow', 'longsword', 'rapier', 'shortsword'],
    toolProficiencies: ['thieves-tools'],
    skillChoices: {
      count: 4,
      from: [
        'acrobatics',
        'athletics',
        'deception',
        'insight',
        'intimidation',
        'investigation',
        'perception',
        'performance',
        'persuasion',
        'sleight-of-hand',
        'stealth',
      ],
    },
    startingEquipment: [
      {
        choose: 1,
        from: [['rapier'], ['shortsword']],
      },
      {
        choose: 1,
        from: [['shortbow', 'quiver', 'arrows-20'], ['shortsword']],
      },
      {
        choose: 1,
        from: [['burglar-pack'], ['dungeoneer-pack'], ['explorer-pack']],
      },
      'leather-armor',
      'dagger',
      'dagger',
      'thieves-tools',
    ],
    features: [
      {
        id: 'expertise-rogue-1',
        name: 'Expertise',
        level: 1,
        description:
          'Choose two of your skill proficiencies. Your proficiency bonus is doubled for any ability check you make that uses either of the chosen proficiencies.',
        choices: [
          {
            type: 'expertise',
            count: 2,
            from: ['any-proficient-skill'],
          },
        ],
      },
      {
        id: 'sneak-attack',
        name: 'Sneak Attack',
        level: 1,
        description:
          'You know how to strike subtly and exploit a foe\'s distraction. Once per turn, you can deal extra 1d6 damage to one creature you hit with an attack if you have advantage on the attack roll.',
      },
      {
        id: 'thieves-cant',
        name: "Thieves' Cant",
        level: 1,
        description:
          'You have learned thieves\' cant, a secret mix of dialect, jargon, and code that allows you to hide messages in seemingly normal conversation.',
      },
      {
        id: 'cunning-action',
        name: 'Cunning Action',
        level: 2,
        description:
          'Your quick thinking and agility allow you to move and act quickly. You can take a bonus action on each of your turns to Dash, Disengage, or Hide.',
      },
      {
        id: 'roguish-archetype',
        name: 'Roguish Archetype',
        level: 3,
        description:
          'You choose an archetype that you emulate in the exercise of your rogue abilities.',
      },
      {
        id: 'asi-4',
        name: 'Ability Score Improvement',
        level: 4,
        description:
          'You can increase one ability score by 2, or you can increase two ability scores by 1.',
      },
      {
        id: 'uncanny-dodge',
        name: 'Uncanny Dodge',
        level: 5,
        description:
          'When an attacker that you can see hits you with an attack, you can use your reaction to halve the attack\'s damage against you.',
      },
      {
        id: 'expertise-rogue-2',
        name: 'Expertise',
        level: 6,
        description:
          'Choose two more of your skill proficiencies. Your proficiency bonus is doubled for these.',
      },
      {
        id: 'evasion',
        name: 'Evasion',
        level: 7,
        description:
          'When you are subjected to an effect that allows you to make a Dexterity saving throw to take only half damage, you instead take no damage on a success and only half damage on a failure.',
      },
      {
        id: 'asi-8',
        name: 'Ability Score Improvement',
        level: 8,
        description:
          'You can increase one ability score by 2, or you can increase two ability scores by 1.',
      },
      {
        id: 'asi-10',
        name: 'Ability Score Improvement',
        level: 10,
        description:
          'You can increase one ability score by 2, or you can increase two ability scores by 1.',
      },
      {
        id: 'reliable-talent',
        name: 'Reliable Talent',
        level: 11,
        description:
          'Whenever you make an ability check that lets you add your proficiency bonus, you can treat a d20 roll of 9 or lower as a 10.',
      },
      {
        id: 'asi-12',
        name: 'Ability Score Improvement',
        level: 12,
        description:
          'You can increase one ability score by 2, or you can increase two ability scores by 1.',
      },
      {
        id: 'blindsense',
        name: 'Blindsense',
        level: 14,
        description:
          'If you are able to hear, you are aware of the location of any hidden or invisible creature within 10 feet of you.',
      },
      {
        id: 'slippery-mind',
        name: 'Slippery Mind',
        level: 15,
        description: 'You have acquired greater mental strength. You gain proficiency in Wisdom saving throws.',
      },
      {
        id: 'asi-16',
        name: 'Ability Score Improvement',
        level: 16,
        description:
          'You can increase one ability score by 2, or you can increase two ability scores by 1.',
      },
      {
        id: 'elusive',
        name: 'Elusive',
        level: 18,
        description:
          'You are so evasive that attackers rarely gain the upper hand against you. No attack roll has advantage against you while you aren\'t incapacitated.',
      },
      {
        id: 'asi-19',
        name: 'Ability Score Improvement',
        level: 19,
        description:
          'You can increase one ability score by 2, or you can increase two ability scores by 1.',
      },
      {
        id: 'stroke-of-luck',
        name: 'Stroke of Luck',
        level: 20,
        description:
          'You have an uncanny knack for succeeding when you need to. If your attack misses a target within range, you can turn the miss into a hit.',
      },
    ],
    subclassLevel: 3,
    subclassName: 'Roguish Archetype',
    subclasses: [
      {
        id: 'thief',
        name: 'Thief',
        description:
          'You hone your skills in the larcenous arts. Burglars, bandits, cutpurses, and other criminals typically follow this archetype.',
        features: [
          {
            id: 'fast-hands',
            name: 'Fast Hands',
            level: 3,
            description:
              'You can use the bonus action granted by your Cunning Action to make a Dexterity (Sleight of Hand) check, use your thieves\' tools to disarm a trap or open a lock, or take the Use an Object action.',
          },
          {
            id: 'second-story-work',
            name: 'Second-Story Work',
            level: 3,
            description:
              'You gain the ability to climb faster than normal; climbing no longer costs you extra movement. Additionally, when you make a running jump, the distance you cover increases by a number of feet equal to your Dexterity modifier.',
          },
          {
            id: 'supreme-sneak',
            name: 'Supreme Sneak',
            level: 9,
            description:
              'You have advantage on a Dexterity (Stealth) check if you move no more than half your speed on the same turn.',
          },
          {
            id: 'use-magic-device',
            name: 'Use Magic Device',
            level: 13,
            description:
              'You have learned enough about the workings of magic that you can improvise the use of items even when they are not intended for you. You ignore all class, race, and level requirements on the use of magic items.',
          },
          {
            id: 'thiefs-reflexes',
            name: "Thief's Reflexes",
            level: 17,
            description:
              'You have become adept at laying ambushes and quickly escaping danger. You can take two turns during the first round of any combat.',
          },
        ],
        source: 'srd-5.1',
      },
      {
        id: 'ignus-thief',
        name: 'Thief',
        description:
          "Ignus version: You hone your skills in the larcenous arts as a professional treasure seeker, explorer, or investigator.",
        features: [],
        source: 'ignus',
      },
      {
        id: 'blade-walker',
        name: 'Blade Walker',
        description:
          "The Blade Walkers specialize in infiltration and assassination, preferring blades up close in the dark.",
        features: [],
        source: 'ignus',
      },
      {
        id: 'dark-scale',
        name: 'Dark Scale',
        description:
          "During The Destruction, these rogues served dragons and became experts in all things draconic.",
        features: [],
        source: 'ignus',
      },
    ],
    multiclassRequirements: [{ ability: 'dexterity', minimum: 13 }],
    multiclassProficiencies: {
      armor: ['light'],
      weapons: [],
      skills: {
        count: 1,
        from: [
          'acrobatics',
          'athletics',
          'deception',
          'insight',
          'intimidation',
          'investigation',
          'perception',
          'performance',
          'persuasion',
          'sleight-of-hand',
          'stealth',
        ],
      },
    },
    source: 'srd-5.1',
  },
  {
    id: 'wizard',
    name: 'Wizard',
    description:
      'A scholarly magic-user capable of manipulating the structures of reality. Wizards are supreme magic-users who study arcane traditions.',
    hitDie: 'd6',
    primaryAbility: ['intelligence'],
    savingThrows: ['intelligence', 'wisdom'],
    armorProficiencies: [],
    weaponProficiencies: ['dagger', 'dart', 'sling', 'quarterstaff', 'light-crossbow'],
    toolProficiencies: [],
    skillChoices: {
      count: 2,
      from: ['arcana', 'history', 'insight', 'investigation', 'medicine', 'religion'],
    },
    startingEquipment: [
      {
        choose: 1,
        from: [['quarterstaff'], ['dagger']],
      },
      {
        choose: 1,
        from: [['component-pouch'], ['arcane-focus']],
      },
      {
        choose: 1,
        from: [['scholar-pack'], ['explorer-pack']],
      },
      'spellbook',
    ],
    features: [
      {
        id: 'spellcasting-wizard',
        name: 'Spellcasting',
        level: 1,
        description:
          'As a student of arcane magic, you have a spellbook containing spells. Intelligence is your spellcasting ability.',
      },
      {
        id: 'arcane-recovery',
        name: 'Arcane Recovery',
        level: 1,
        description:
          'You have learned to regain some of your magical energy by studying your spellbook. Once per day when you finish a short rest, you can choose expended spell slots to recover.',
      },
      {
        id: 'arcane-tradition',
        name: 'Arcane Tradition',
        level: 2,
        description:
          'You choose an arcane tradition, shaping your practice of magic through one of the schools.',
      },
      {
        id: 'asi-4',
        name: 'Ability Score Improvement',
        level: 4,
        description:
          'You can increase one ability score by 2, or you can increase two ability scores by 1.',
      },
      {
        id: 'asi-8',
        name: 'Ability Score Improvement',
        level: 8,
        description:
          'You can increase one ability score by 2, or you can increase two ability scores by 1.',
      },
      {
        id: 'asi-12',
        name: 'Ability Score Improvement',
        level: 12,
        description:
          'You can increase one ability score by 2, or you can increase two ability scores by 1.',
      },
      {
        id: 'asi-16',
        name: 'Ability Score Improvement',
        level: 16,
        description:
          'You can increase one ability score by 2, or you can increase two ability scores by 1.',
      },
      {
        id: 'spell-mastery',
        name: 'Spell Mastery',
        level: 18,
        description:
          'You have achieved such mastery over certain spells that you can cast them at will. Choose a 1st-level wizard spell and a 2nd-level wizard spell in your spellbook. You can cast those spells without expending a spell slot.',
      },
      {
        id: 'asi-19',
        name: 'Ability Score Improvement',
        level: 19,
        description:
          'You can increase one ability score by 2, or you can increase two ability scores by 1.',
      },
      {
        id: 'signature-spells',
        name: 'Signature Spells',
        level: 20,
        description:
          'You gain mastery over two powerful spells and can cast them with little effort. Choose two 3rd-level wizard spells in your spellbook. You always have these spells prepared, and you can cast each once without expending a spell slot.',
      },
    ],
    spellcasting: {
      type: 'full',
      ability: 'intelligence',
      ritual: true,
      focus: 'arcane focus',
      cantripsKnown: [3, 3, 3, 4, 4, 4, 4, 4, 4, 5, 5, 5, 5, 5, 5, 5, 5, 5, 5, 5],
      spellSlots: [
        [2, 0, 0, 0, 0, 0, 0, 0, 0],
        [3, 0, 0, 0, 0, 0, 0, 0, 0],
        [4, 2, 0, 0, 0, 0, 0, 0, 0],
        [4, 3, 0, 0, 0, 0, 0, 0, 0],
        [4, 3, 2, 0, 0, 0, 0, 0, 0],
        [4, 3, 3, 0, 0, 0, 0, 0, 0],
        [4, 3, 3, 1, 0, 0, 0, 0, 0],
        [4, 3, 3, 2, 0, 0, 0, 0, 0],
        [4, 3, 3, 3, 1, 0, 0, 0, 0],
        [4, 3, 3, 3, 2, 0, 0, 0, 0],
        [4, 3, 3, 3, 2, 1, 0, 0, 0],
        [4, 3, 3, 3, 2, 1, 0, 0, 0],
        [4, 3, 3, 3, 2, 1, 1, 0, 0],
        [4, 3, 3, 3, 2, 1, 1, 0, 0],
        [4, 3, 3, 3, 2, 1, 1, 1, 0],
        [4, 3, 3, 3, 2, 1, 1, 1, 0],
        [4, 3, 3, 3, 2, 1, 1, 1, 1],
        [4, 3, 3, 3, 3, 1, 1, 1, 1],
        [4, 3, 3, 3, 3, 2, 1, 1, 1],
        [4, 3, 3, 3, 3, 2, 2, 1, 1],
      ],
    },
    subclassLevel: 2,
    subclassName: 'Arcane Tradition',
    subclasses: [
      {
        id: 'evocation',
        name: 'School of Evocation',
        description:
          'You focus your study on magic that creates powerful elemental effects such as bitter cold, searing flame, rolling thunder, and crackling lightning.',
        features: [
          {
            id: 'evocation-savant',
            name: 'Evocation Savant',
            level: 2,
            description:
              'The gold and time you must spend to copy an evocation spell into your spellbook is halved.',
          },
          {
            id: 'sculpt-spells',
            name: 'Sculpt Spells',
            level: 2,
            description:
              'You can create pockets of relative safety within the effects of your evocation spells. When you cast an evocation spell, you can choose a number of creatures equal to 1 + the spell\'s level to automatically succeed on their saving throws.',
          },
          {
            id: 'potent-cantrip',
            name: 'Potent Cantrip',
            level: 6,
            description:
              'Your damaging cantrips affect even creatures that avoid the brunt of the effect. When a creature succeeds on a saving throw against your cantrip, the creature takes half the cantrip\'s damage.',
          },
          {
            id: 'empowered-evocation',
            name: 'Empowered Evocation',
            level: 10,
            description:
              'You can add your Intelligence modifier to one damage roll of any wizard evocation spell you cast.',
          },
          {
            id: 'overchannel',
            name: 'Overchannel',
            level: 14,
            description:
              'You can increase the power of your simpler spells. When you cast a wizard spell of 1st through 5th level that deals damage, you can deal maximum damage with that spell.',
          },
        ],
        source: 'srd-5.1',
      },
      {
        id: 'ignus-evocation',
        name: 'School of Evocation',
        description:
          "Ignus version: You focus your study on magic that creates powerful elemental effects.",
        features: [],
        source: 'ignus',
      },
      {
        id: 'elementalist',
        name: 'Elementalist',
        description:
          "Elementalists master the raw power of the elements.",
        features: [],
        source: 'ignus',
      },
      {
        id: 'archivist',
        name: 'Archivist',
        description:
          "Archivists are masters of magical knowledge and lore.",
        features: [],
        source: 'ignus',
      },
    ],
    multiclassRequirements: [{ ability: 'intelligence', minimum: 13 }],
    multiclassProficiencies: {
      armor: [],
      weapons: [],
    },
    source: 'srd-5.1',
  },
  {
    id: 'bard',
    name: 'Bard',
    description:
      'An inspiring magician whose power echoes the music of creation. Bards use their artistic talents to weave magic.',
    hitDie: 'd8',
    primaryAbility: ['charisma'],
    savingThrows: ['dexterity', 'charisma'],
    armorProficiencies: ['light'],
    weaponProficiencies: ['simple', 'hand crossbow', 'longsword', 'rapier', 'shortsword'],
    toolProficiencies: ['three musical instruments'],
    skillChoices: {
      count: 3,
      from: [
        'acrobatics',
        'animal-handling',
        'arcana',
        'athletics',
        'deception',
        'history',
        'insight',
        'intimidation',
        'investigation',
        'medicine',
        'nature',
        'perception',
        'performance',
        'persuasion',
        'religion',
        'sleight-of-hand',
        'stealth',
        'survival',
      ],
    },
    startingEquipment: [
      {
        choose: 1,
        from: [['rapier'], ['longsword'], ['any-simple']],
      },
      {
        choose: 1,
        from: [['diplomat-pack'], ['entertainer-pack']],
      },
      {
        choose: 1,
        from: [['lute'], ['any-musical-instrument']],
      },
      'leather-armor',
      'dagger',
    ],
    features: [
      {
        id: 'spellcasting-bard',
        name: 'Spellcasting',
        level: 1,
        description:
          'You have learned to untangle and reshape the fabric of reality in harmony with your wishes and music. Your spells are part of your vast repertoire. Charisma is your spellcasting ability.',
      },
      {
        id: 'bardic-inspiration',
        name: 'Bardic Inspiration',
        level: 1,
        description:
          'You can inspire others through stirring words or music. As a bonus action, choose one creature within 60 feet who can hear you. That creature gains a Bardic Inspiration die (d6). The die increases at higher levels.',
      },
      {
        id: 'jack-of-all-trades',
        name: 'Jack of All Trades',
        level: 2,
        description:
          'You can add half your proficiency bonus, rounded down, to any ability check you make that doesn\'t already include your proficiency bonus.',
      },
      {
        id: 'song-of-rest',
        name: 'Song of Rest',
        level: 2,
        description:
          'You can use soothing music or oration to help revitalize your wounded allies during a short rest. If you or any friendly creatures who can hear your performance regain hit points at the end of the short rest by spending one or more Hit Dice, each of those creatures regains an extra 1d6 hit points.',
      },
      {
        id: 'bard-college',
        name: 'Bard College',
        level: 3,
        description:
          'You delve into the advanced techniques of a bard college of your choice. Your choice grants you features at 3rd level and again at 6th and 14th level.',
      },
      {
        id: 'expertise-bard',
        name: 'Expertise',
        level: 3,
        description:
          'Choose two of your skill proficiencies. Your proficiency bonus is doubled for any ability check you make that uses either of the chosen proficiencies. At 10th level, you can choose another two skill proficiencies to gain this benefit.',
        choices: [
          {
            type: 'expertise',
            count: 2,
            from: ['any-proficient-skill'],
          },
        ],
      },
      {
        id: 'asi-bard-4',
        name: 'Ability Score Improvement',
        level: 4,
        description:
          'You can increase one ability score by 2, or you can increase two ability scores by 1.',
      },
      {
        id: 'font-of-inspiration',
        name: 'Font of Inspiration',
        level: 5,
        description:
          'You regain all of your expended uses of Bardic Inspiration when you finish a short or long rest.',
      },
      {
        id: 'countercharm',
        name: 'Countercharm',
        level: 6,
        description:
          'You gain the ability to use musical notes or words of power to disrupt mind-influencing effects. As an action, you can start a performance that lasts until the end of your next turn. During that time, you and any friendly creatures within 30 feet of you have advantage on saving throws against being frightened or charmed.',
      },
      {
        id: 'asi-bard-8',
        name: 'Ability Score Improvement',
        level: 8,
        description:
          'You can increase one ability score by 2, or you can increase two ability scores by 1.',
      },
      {
        id: 'magical-secrets-1',
        name: 'Magical Secrets',
        level: 10,
        description:
          'You have plundered magical knowledge from a wide spectrum of disciplines. Choose two spells from any classes, including this one. The chosen spells count as bard spells for you.',
      },
      {
        id: 'expertise-bard-2',
        name: 'Expertise',
        level: 10,
        description:
          'Choose two more of your skill proficiencies. Your proficiency bonus is doubled for these.',
        choices: [
          {
            type: 'expertise',
            count: 2,
            from: ['any-proficient-skill'],
          },
        ],
      },
      {
        id: 'asi-bard-12',
        name: 'Ability Score Improvement',
        level: 12,
        description:
          'You can increase one ability score by 2, or you can increase two ability scores by 1.',
      },
      {
        id: 'magical-secrets-2',
        name: 'Magical Secrets',
        level: 14,
        description:
          'Choose two additional spells from any classes.',
      },
      {
        id: 'asi-bard-16',
        name: 'Ability Score Improvement',
        level: 16,
        description:
          'You can increase one ability score by 2, or you can increase two ability scores by 1.',
      },
      {
        id: 'magical-secrets-3',
        name: 'Magical Secrets',
        level: 18,
        description:
          'Choose two additional spells from any classes.',
      },
      {
        id: 'asi-bard-19',
        name: 'Ability Score Improvement',
        level: 19,
        description:
          'You can increase one ability score by 2, or you can increase two ability scores by 1.',
      },
      {
        id: 'superior-inspiration',
        name: 'Superior Inspiration',
        level: 20,
        description:
          'When you roll initiative and have no uses of Bardic Inspiration left, you regain one use.',
      },
    ],
    spellcasting: {
      ability: 'charisma',
      type: 'full',
      ritual: true,
      focus: 'musical instrument',
      cantripsKnown: [2, 2, 2, 3, 3, 3, 3, 3, 3, 4, 4, 4, 4, 4, 4, 4, 4, 4, 4, 4],
      spellsKnown: [4, 5, 6, 7, 8, 9, 10, 11, 12, 14, 15, 15, 16, 18, 19, 19, 20, 22, 22, 22],
      spellSlots: [
        [2, 0, 0, 0, 0, 0, 0, 0, 0],
        [3, 0, 0, 0, 0, 0, 0, 0, 0],
        [4, 2, 0, 0, 0, 0, 0, 0, 0],
        [4, 3, 0, 0, 0, 0, 0, 0, 0],
        [4, 3, 2, 0, 0, 0, 0, 0, 0],
        [4, 3, 3, 0, 0, 0, 0, 0, 0],
        [4, 3, 3, 1, 0, 0, 0, 0, 0],
        [4, 3, 3, 2, 0, 0, 0, 0, 0],
        [4, 3, 3, 3, 1, 0, 0, 0, 0],
        [4, 3, 3, 3, 2, 0, 0, 0, 0],
        [4, 3, 3, 3, 2, 1, 0, 0, 0],
        [4, 3, 3, 3, 2, 1, 0, 0, 0],
        [4, 3, 3, 3, 2, 1, 1, 0, 0],
        [4, 3, 3, 3, 2, 1, 1, 0, 0],
        [4, 3, 3, 3, 2, 1, 1, 1, 0],
        [4, 3, 3, 3, 2, 1, 1, 1, 0],
        [4, 3, 3, 3, 2, 1, 1, 1, 1],
        [4, 3, 3, 3, 3, 1, 1, 1, 1],
        [4, 3, 3, 3, 3, 2, 1, 1, 1],
        [4, 3, 3, 3, 3, 2, 2, 1, 1],
      ],
    },
    subclassLevel: 3,
    subclassName: 'Bard College',
    subclasses: [
      {
        id: 'college-of-lore',
        name: 'College of Lore',
        description:
          'Bards of the College of Lore know something about most things, collecting bits of knowledge from sources as diverse as scholarly tomes and peasant tales.',
        features: [
          {
            id: 'bonus-proficiencies-lore',
            name: 'Bonus Proficiencies',
            level: 3,
            description:
              'When you join the College of Lore at 3rd level, you gain proficiency with three skills of your choice.',
          },
          {
            id: 'cutting-words',
            name: 'Cutting Words',
            level: 3,
            description:
              'You learn how to use your wit to distract, confuse, and otherwise sap the confidence and competence of others. When a creature that you can see within 60 feet of you makes an attack roll, an ability check, or a damage roll, you can use your reaction to expend one of your uses of Bardic Inspiration, rolling a Bardic Inspiration die and subtracting the number rolled from the creature\'s roll.',
          },
          {
            id: 'additional-magical-secrets',
            name: 'Additional Magical Secrets',
            level: 6,
            description:
              'You learn two spells of your choice from any class. The spells you choose must be of a level you can cast, and they count as bard spells for you but don\'t count against the number of bard spells you know.',
          },
          {
            id: 'peerless-skill',
            name: 'Peerless Skill',
            level: 14,
            description:
              'When you make an ability check, you can expend one use of Bardic Inspiration. Roll a Bardic Inspiration die and add the number rolled to your ability check.',
          },
        ],
        source: 'srd-5.1',
      },
      {
        id: 'ignus-lore',
        name: 'College of Lore',
        description:
          "Ignus version: Bards of the College of Lore know something about most things, collecting bits of knowledge from sources as diverse as scholarly tomes and peasant tales.",
        features: [],
        source: 'ignus',
      },
      {
        id: 'leadership',
        name: 'College of Leadership',
        description:
          "Masters of propaganda and manipulation, the bards of the College of Leadership are gifted with a special mark called the Servant's Mark.",
        features: [],
        source: 'ignus',
      },
      {
        id: 'engineering',
        name: 'College of Engineering',
        description:
          "Bards of the College of Engineering combine technical knowledge with artistic expression, creating wonders of innovation.",
        features: [],
        source: 'ignus',
      },
    ],
    multiclassRequirements: [{ ability: 'charisma', minimum: 13 }],
    multiclassProficiencies: {
      armor: ['light'],
      weapons: [],
      skills: { count: 1, from: ['acrobatics', 'animal-handling', 'arcana', 'athletics', 'deception', 'history', 'insight', 'intimidation', 'investigation', 'medicine', 'nature', 'perception', 'performance', 'persuasion', 'religion', 'sleight-of-hand', 'stealth', 'survival'] },
    },
    source: 'srd-5.1',
  },
  {
    id: 'druid',
    name: 'Druid',
    description:
      'A priest of the Old Faith, wielding the powers of nature and adopting animal forms. Druids revere nature above all.',
    hitDie: 'd8',
    primaryAbility: ['wisdom'],
    savingThrows: ['intelligence', 'wisdom'],
    armorProficiencies: ['light', 'medium', 'shield'],
    weaponProficiencies: ['club', 'dagger', 'dart', 'javelin', 'mace', 'quarterstaff', 'scimitar', 'sickle', 'sling', 'spear'],
    toolProficiencies: ['herbalism kit'],
    skillChoices: {
      count: 2,
      from: [
        'arcana',
        'animal-handling',
        'insight',
        'medicine',
        'nature',
        'perception',
        'religion',
        'survival',
      ],
    },
    startingEquipment: [
      {
        choose: 1,
        from: [['wooden-shield'], ['any-simple']],
      },
      {
        choose: 1,
        from: [['scimitar'], ['any-simple-melee']],
      },
      'leather-armor',
      'explorer-pack',
      'druidic-focus',
    ],
    features: [
      {
        id: 'druidic',
        name: 'Druidic',
        level: 1,
        description:
          'You know Druidic, the secret language of druids. You can speak the language and use it to leave hidden messages.',
      },
      {
        id: 'spellcasting-druid',
        name: 'Spellcasting',
        level: 1,
        description:
          'Drawing on the divine essence of nature itself, you can cast spells to shape that essence to your will. Wisdom is your spellcasting ability.',
      },
      {
        id: 'wild-shape',
        name: 'Wild Shape',
        level: 2,
        description:
          'You can use your action to magically assume the shape of a beast that you have seen before. You can use this feature twice per short or long rest.',
      },
      {
        id: 'druid-circle',
        name: 'Druid Circle',
        level: 2,
        description:
          'You choose to identify with a circle of druids. Your choice grants you features at 2nd level and again at 6th, 10th, and 14th level.',
      },
      {
        id: 'asi-druid-4',
        name: 'Ability Score Improvement',
        level: 4,
        description:
          'You can increase one ability score by 2, or you can increase two ability scores by 1.',
      },
      {
        id: 'asi-druid-8',
        name: 'Ability Score Improvement',
        level: 8,
        description:
          'You can increase one ability score by 2, or you can increase two ability scores by 1.',
      },
      {
        id: 'asi-druid-12',
        name: 'Ability Score Improvement',
        level: 12,
        description:
          'You can increase one ability score by 2, or you can increase two ability scores by 1.',
      },
      {
        id: 'asi-druid-16',
        name: 'Ability Score Improvement',
        level: 16,
        description:
          'You can increase one ability score by 2, or you can increase two ability scores by 1.',
      },
      {
        id: 'timeless-body-druid',
        name: 'Timeless Body',
        level: 18,
        description:
          'The primal magic that you wield causes you to age more slowly. For every 10 years that pass, your body ages only 1 year.',
      },
      {
        id: 'beast-spells',
        name: 'Beast Spells',
        level: 18,
        description:
          'You can cast many of your druid spells in any shape you assume using Wild Shape. You can perform the somatic and verbal components of a druid spell while in a beast shape.',
      },
      {
        id: 'asi-druid-19',
        name: 'Ability Score Improvement',
        level: 19,
        description:
          'You can increase one ability score by 2, or you can increase two ability scores by 1.',
      },
      {
        id: 'archdruid',
        name: 'Archdruid',
        level: 20,
        description:
          'You can use your Wild Shape an unlimited number of times. Additionally, you can ignore the verbal and somatic components of your druid spells, as well as any material components that lack a cost.',
      },
    ],
    spellcasting: {
      ability: 'wisdom',
      type: 'full',
      ritual: true,
      focus: 'druidic focus',
      cantripsKnown: [2, 2, 2, 3, 3, 3, 3, 3, 3, 4, 4, 4, 4, 4, 4, 4, 4, 4, 4, 4],
      spellSlots: [
        [2, 0, 0, 0, 0, 0, 0, 0, 0],
        [3, 0, 0, 0, 0, 0, 0, 0, 0],
        [4, 2, 0, 0, 0, 0, 0, 0, 0],
        [4, 3, 0, 0, 0, 0, 0, 0, 0],
        [4, 3, 2, 0, 0, 0, 0, 0, 0],
        [4, 3, 3, 0, 0, 0, 0, 0, 0],
        [4, 3, 3, 1, 0, 0, 0, 0, 0],
        [4, 3, 3, 2, 0, 0, 0, 0, 0],
        [4, 3, 3, 3, 1, 0, 0, 0, 0],
        [4, 3, 3, 3, 2, 0, 0, 0, 0],
        [4, 3, 3, 3, 2, 1, 0, 0, 0],
        [4, 3, 3, 3, 2, 1, 0, 0, 0],
        [4, 3, 3, 3, 2, 1, 1, 0, 0],
        [4, 3, 3, 3, 2, 1, 1, 0, 0],
        [4, 3, 3, 3, 2, 1, 1, 1, 0],
        [4, 3, 3, 3, 2, 1, 1, 1, 0],
        [4, 3, 3, 3, 2, 1, 1, 1, 1],
        [4, 3, 3, 3, 3, 1, 1, 1, 1],
        [4, 3, 3, 3, 3, 2, 1, 1, 1],
        [4, 3, 3, 3, 3, 2, 2, 1, 1],
      ],
    },
    subclassLevel: 2,
    subclassName: 'Druid Circle',
    subclasses: [
      {
        id: 'circle-of-the-land',
        name: 'Circle of the Land',
        description:
          'The Circle of the Land is made up of mystics and sages who safeguard ancient knowledge and rites through a vast oral tradition.',
        features: [
          {
            id: 'bonus-cantrip-land',
            name: 'Bonus Cantrip',
            level: 2,
            description:
              'You learn one additional druid cantrip of your choice.',
          },
          {
            id: 'natural-recovery',
            name: 'Natural Recovery',
            level: 2,
            description:
              'During a short rest, you can recover expended spell slots. The spell slots can have a combined level that is equal to or less than half your druid level (rounded up).',
          },
          {
            id: 'circle-spells',
            name: 'Circle Spells',
            level: 3,
            description:
              'Your mystical connection to the land infuses you with the ability to cast certain spells. You gain access to circle spells connected to the land where you became a druid.',
          },
          {
            id: 'lands-stride',
            name: "Land's Stride",
            level: 6,
            description:
              'Moving through nonmagical difficult terrain costs you no extra movement. You can also pass through nonmagical plants without being slowed by them.',
          },
          {
            id: 'natures-ward',
            name: "Nature's Ward",
            level: 10,
            description:
              'You can\'t be charmed or frightened by elementals or fey, and you are immune to poison and disease.',
          },
          {
            id: 'natures-sanctuary',
            name: "Nature's Sanctuary",
            level: 14,
            description:
              'Creatures of the natural world sense your connection to nature and become hesitant to attack you. When a beast or plant creature attacks you, it must make a Wisdom saving throw against your druid spell save DC.',
          },
        ],
        source: 'srd-5.1',
      },
      {
        id: 'ignus-land',
        name: 'Circle of the Land',
        description:
          "Ignus version: The Circle of the Land is made up of mystics and sages who safeguard ancient knowledge and rites through a vast oral tradition.",
        features: [],
        source: 'ignus',
      },
      {
        id: 'decay',
        name: 'Circle of Decay',
        description:
          "The Circle of Decay druids are servants of nature's cycle of life and death.",
        features: [],
        source: 'ignus',
      },
      {
        id: 'night',
        name: 'Circle of the Night',
        description:
          "Druids of the Circle of the Night look towards the heavens for inspiration and guidance, reading Ignus's nighttime auroras.",
        features: [],
        source: 'ignus',
      },
    ],
    multiclassRequirements: [{ ability: 'wisdom', minimum: 13 }],
    multiclassProficiencies: {
      armor: ['light', 'medium', 'shield'],
      weapons: [],
    },
    source: 'srd-5.1',
  },
  {
    id: 'monk',
    name: 'Monk',
    description:
      'A master of martial arts, harnessing the power of the body in pursuit of physical and spiritual perfection.',
    hitDie: 'd8',
    primaryAbility: ['dexterity', 'wisdom'],
    savingThrows: ['strength', 'dexterity'],
    armorProficiencies: [],
    weaponProficiencies: ['simple', 'shortsword'],
    toolProficiencies: ['one artisan tool or musical instrument'],
    skillChoices: {
      count: 2,
      from: [
        'acrobatics',
        'athletics',
        'history',
        'insight',
        'religion',
        'stealth',
      ],
    },
    startingEquipment: [
      {
        choose: 1,
        from: [['shortsword'], ['any-simple']],
      },
      {
        choose: 1,
        from: [['dungeoneer-pack'], ['explorer-pack']],
      },
      'dart',
      'dart',
      'dart',
      'dart',
      'dart',
      'dart',
      'dart',
      'dart',
      'dart',
      'dart',
    ],
    features: [
      {
        id: 'unarmored-defense-monk',
        name: 'Unarmored Defense',
        level: 1,
        description:
          'While you are wearing no armor and not wielding a shield, your AC equals 10 + your Dexterity modifier + your Wisdom modifier.',
      },
      {
        id: 'martial-arts',
        name: 'Martial Arts',
        level: 1,
        description:
          'Your practice of martial arts gives you mastery of combat styles that use unarmed strikes and monk weapons. You can use Dexterity instead of Strength for attack and damage rolls, and you can roll a d4 in place of normal damage.',
      },
      {
        id: 'ki',
        name: 'Ki',
        level: 2,
        description:
          'Your training allows you to harness the mystic energy of ki. You have a number of ki points equal to your monk level. You can spend these points to fuel various ki features: Flurry of Blows, Patient Defense, and Step of the Wind.',
      },
      {
        id: 'unarmored-movement',
        name: 'Unarmored Movement',
        level: 2,
        description:
          'Your speed increases by 10 feet while you are not wearing armor or wielding a shield. This bonus increases as you gain monk levels.',
      },
      {
        id: 'monastic-tradition',
        name: 'Monastic Tradition',
        level: 3,
        description:
          'You commit yourself to a monastic tradition. Your tradition grants you features at 3rd level and again at 6th, 11th, and 17th level.',
      },
      {
        id: 'deflect-missiles',
        name: 'Deflect Missiles',
        level: 3,
        description:
          'You can use your reaction to deflect or catch the missile when you are hit by a ranged weapon attack. The damage you take is reduced by 1d10 + your Dexterity modifier + your monk level.',
      },
      {
        id: 'asi-monk-4',
        name: 'Ability Score Improvement',
        level: 4,
        description:
          'You can increase one ability score by 2, or you can increase two ability scores by 1.',
      },
      {
        id: 'slow-fall',
        name: 'Slow Fall',
        level: 4,
        description:
          'You can use your reaction when you fall to reduce any falling damage you take by an amount equal to five times your monk level.',
      },
      {
        id: 'extra-attack-monk',
        name: 'Extra Attack',
        level: 5,
        description:
          'You can attack twice, instead of once, whenever you take the Attack action on your turn.',
      },
      {
        id: 'stunning-strike',
        name: 'Stunning Strike',
        level: 5,
        description:
          'You can interfere with the flow of ki in an opponent\'s body. When you hit another creature with a melee weapon attack, you can spend 1 ki point to attempt a stunning strike. The target must succeed on a Constitution saving throw or be stunned until the end of your next turn.',
      },
      {
        id: 'ki-empowered-strikes',
        name: 'Ki-Empowered Strikes',
        level: 6,
        description:
          'Your unarmed strikes count as magical for the purpose of overcoming resistance and immunity to nonmagical attacks and damage.',
      },
      {
        id: 'evasion-monk',
        name: 'Evasion',
        level: 7,
        description:
          'When you are subjected to an effect that allows you to make a Dexterity saving throw to take only half damage, you instead take no damage if you succeed on the saving throw, and only half damage if you fail.',
      },
      {
        id: 'stillness-of-mind',
        name: 'Stillness of Mind',
        level: 7,
        description:
          'You can use your action to end one effect on yourself that is causing you to be charmed or frightened.',
      },
      {
        id: 'asi-monk-8',
        name: 'Ability Score Improvement',
        level: 8,
        description:
          'You can increase one ability score by 2, or you can increase two ability scores by 1.',
      },
      {
        id: 'unarmored-movement-improvement',
        name: 'Unarmored Movement Improvement',
        level: 9,
        description:
          'You gain the ability to move along vertical surfaces and across liquids on your turn without falling during the move.',
      },
      {
        id: 'purity-of-body',
        name: 'Purity of Body',
        level: 10,
        description:
          'Your mastery of the ki flowing through you makes you immune to disease and poison.',
      },
      {
        id: 'asi-monk-12',
        name: 'Ability Score Improvement',
        level: 12,
        description:
          'You can increase one ability score by 2, or you can increase two ability scores by 1.',
      },
      {
        id: 'tongue-of-sun-and-moon',
        name: 'Tongue of the Sun and Moon',
        level: 13,
        description:
          'You learn to touch the ki of other minds so that you understand all spoken languages. Moreover, any creature that can understand a language can understand what you say.',
      },
      {
        id: 'diamond-soul',
        name: 'Diamond Soul',
        level: 14,
        description:
          'Your mastery of ki grants you proficiency in all saving throws. Additionally, whenever you make a saving throw and fail, you can spend 1 ki point to reroll it and take the second result.',
      },
      {
        id: 'timeless-body-monk',
        name: 'Timeless Body',
        level: 15,
        description:
          'Your ki sustains you so that you suffer none of the frailty of old age, and you can\'t be aged magically. You can still die of old age. You no longer need food or water.',
      },
      {
        id: 'asi-monk-16',
        name: 'Ability Score Improvement',
        level: 16,
        description:
          'You can increase one ability score by 2, or you can increase two ability scores by 1.',
      },
      {
        id: 'empty-body',
        name: 'Empty Body',
        level: 18,
        description:
          'You can use your action to spend 4 ki points to become invisible for 1 minute. During that time, you have resistance to all damage but force damage.',
      },
      {
        id: 'asi-monk-19',
        name: 'Ability Score Improvement',
        level: 19,
        description:
          'You can increase one ability score by 2, or you can increase two ability scores by 1.',
      },
      {
        id: 'perfect-self',
        name: 'Perfect Self',
        level: 20,
        description:
          'When you roll for initiative and have no ki points remaining, you regain 4 ki points.',
      },
    ],
    subclassLevel: 3,
    subclassName: 'Monastic Tradition',
    subclasses: [
      {
        id: 'way-of-the-open-hand',
        name: 'Way of the Open Hand',
        description:
          'Monks of the Way of the Open Hand are the ultimate masters of martial arts combat, whether armed or unarmed.',
        features: [
          {
            id: 'open-hand-technique',
            name: 'Open Hand Technique',
            level: 3,
            description:
              'Whenever you hit a creature with one of the attacks granted by your Flurry of Blows, you can impose one of the following effects: knock prone, push 15 feet, or prevent reactions.',
          },
          {
            id: 'wholeness-of-body',
            name: 'Wholeness of Body',
            level: 6,
            description:
              'You gain the ability to heal yourself. As an action, you can regain hit points equal to three times your monk level. You must finish a long rest before you can use this feature again.',
          },
          {
            id: 'tranquility',
            name: 'Tranquility',
            level: 11,
            description:
              'You can enter a special meditation that surrounds you with an aura of peace. At the end of a long rest, you gain the effect of a sanctuary spell that lasts until the start of your next long rest.',
          },
          {
            id: 'quivering-palm',
            name: 'Quivering Palm',
            level: 17,
            description:
              'You gain the ability to set up lethal vibrations in someone\'s body. When you hit a creature with an unarmed strike, you can spend 3 ki points to start these imperceptible vibrations, which last for a number of days equal to your monk level. You can then use an action to end the vibrations, causing the target to make a Constitution saving throw or be reduced to 0 hit points.',
          },
        ],
        source: 'srd-5.1',
      },
      {
        id: 'ignus-open-hand',
        name: 'Way of the Open Hand',
        description:
          "Ignus version: Monks of the Way of the Open Hand are the ultimate masters of martial arts combat.",
        features: [],
        source: 'ignus',
      },
      {
        id: 'teacher',
        name: 'Way of the Teacher',
        description:
          "Monks of the Way of the Teacher are servants of the people, sharing knowledge and empowering others.",
        features: [],
        source: 'ignus',
      },
      {
        id: 'knife',
        name: 'Way of the Knife',
        description:
          "Monks who follow the Way of the Knife train their bodies and focus their ki to become hardened weapons of war.",
        features: [],
        source: 'ignus',
      },
    ],
    multiclassRequirements: [
      { ability: 'dexterity', minimum: 13 },
      { ability: 'wisdom', minimum: 13 },
    ],
    multiclassProficiencies: {
      armor: [],
      weapons: ['simple', 'shortsword'],
    },
    source: 'srd-5.1',
  },
  {
    id: 'paladin',
    name: 'Paladin',
    description:
      'A holy warrior bound to a sacred oath, paladins use divine magic to smite their enemies and protect the innocent.',
    hitDie: 'd10',
    primaryAbility: ['strength', 'charisma'],
    savingThrows: ['wisdom', 'charisma'],
    armorProficiencies: ['light', 'medium', 'heavy', 'shield'],
    weaponProficiencies: ['simple', 'martial'],
    toolProficiencies: [],
    skillChoices: {
      count: 2,
      from: [
        'athletics',
        'insight',
        'intimidation',
        'medicine',
        'persuasion',
        'religion',
      ],
    },
    startingEquipment: [
      {
        choose: 1,
        from: [['any-martial', 'shield'], ['any-martial', 'any-martial']],
      },
      {
        choose: 1,
        from: [['javelin', 'javelin', 'javelin', 'javelin', 'javelin'], ['any-simple-melee']],
      },
      {
        choose: 1,
        from: [['priest-pack'], ['explorer-pack']],
      },
      'chain-mail',
      'holy-symbol',
    ],
    features: [
      {
        id: 'divine-sense',
        name: 'Divine Sense',
        level: 1,
        description:
          'The presence of strong evil registers on your senses like a noxious odor. As an action, you can detect the location of any celestial, fiend, or undead within 60 feet of you that is not behind total cover.',
      },
      {
        id: 'lay-on-hands',
        name: 'Lay on Hands',
        level: 1,
        description:
          'Your blessed touch can heal wounds. You have a pool of healing power that replenishes when you take a long rest. With that pool, you can restore a total number of hit points equal to your paladin level × 5.',
      },
      {
        id: 'fighting-style-paladin',
        name: 'Fighting Style',
        level: 2,
        description:
          'You adopt a particular style of fighting as your specialty. Choose one of the following options: Defense, Dueling, Great Weapon Fighting, or Protection.',
      },
      {
        id: 'spellcasting-paladin',
        name: 'Spellcasting',
        level: 2,
        description:
          'You have learned to draw on divine magic through meditation and prayer to cast spells. Charisma is your spellcasting ability.',
      },
      {
        id: 'divine-smite',
        name: 'Divine Smite',
        level: 2,
        description:
          'When you hit a creature with a melee weapon attack, you can expend one spell slot to deal radiant damage to the target, in addition to the weapon\'s damage. The extra damage is 2d8 for a 1st-level slot, plus 1d8 for each spell level higher than 1st, to a maximum of 5d8.',
      },
      {
        id: 'divine-health',
        name: 'Divine Health',
        level: 3,
        description:
          'The divine magic flowing through you makes you immune to disease.',
      },
      {
        id: 'sacred-oath',
        name: 'Sacred Oath',
        level: 3,
        description:
          'You swear the oath that binds you as a paladin forever. Your choice grants you features at 3rd level and again at 7th, 15th, and 20th level.',
      },
      {
        id: 'asi-paladin-4',
        name: 'Ability Score Improvement',
        level: 4,
        description:
          'You can increase one ability score by 2, or you can increase two ability scores by 1.',
      },
      {
        id: 'extra-attack-paladin',
        name: 'Extra Attack',
        level: 5,
        description:
          'You can attack twice, instead of once, whenever you take the Attack action on your turn.',
      },
      {
        id: 'aura-of-protection',
        name: 'Aura of Protection',
        level: 6,
        description:
          'Whenever you or a friendly creature within 10 feet of you must make a saving throw, the creature gains a bonus to the saving throw equal to your Charisma modifier (minimum of +1). At 18th level, the range increases to 30 feet.',
      },
      {
        id: 'asi-paladin-8',
        name: 'Ability Score Improvement',
        level: 8,
        description:
          'You can increase one ability score by 2, or you can increase two ability scores by 1.',
      },
      {
        id: 'aura-of-courage',
        name: 'Aura of Courage',
        level: 10,
        description:
          'You and friendly creatures within 10 feet of you can\'t be frightened while you are conscious. At 18th level, the range increases to 30 feet.',
      },
      {
        id: 'improved-divine-smite',
        name: 'Improved Divine Smite',
        level: 11,
        description:
          'You are so suffused with righteous might that all your melee weapon strikes carry divine power with them. Whenever you hit a creature with a melee weapon, the creature takes an extra 1d8 radiant damage.',
      },
      {
        id: 'asi-paladin-12',
        name: 'Ability Score Improvement',
        level: 12,
        description:
          'You can increase one ability score by 2, or you can increase two ability scores by 1.',
      },
      {
        id: 'cleansing-touch',
        name: 'Cleansing Touch',
        level: 14,
        description:
          'You can use your action to end one spell on yourself or on one willing creature that you touch. You can use this feature a number of times equal to your Charisma modifier (minimum of once).',
      },
      {
        id: 'asi-paladin-16',
        name: 'Ability Score Improvement',
        level: 16,
        description:
          'You can increase one ability score by 2, or you can increase two ability scores by 1.',
      },
      {
        id: 'asi-paladin-19',
        name: 'Ability Score Improvement',
        level: 19,
        description:
          'You can increase one ability score by 2, or you can increase two ability scores by 1.',
      },
    ],
    spellcasting: {
      ability: 'charisma',
      type: 'half',
      ritual: false,
      focus: 'holy symbol',
      spellSlots: [
        [0, 0, 0, 0, 0, 0, 0, 0, 0],
        [2, 0, 0, 0, 0, 0, 0, 0, 0],
        [3, 0, 0, 0, 0, 0, 0, 0, 0],
        [3, 0, 0, 0, 0, 0, 0, 0, 0],
        [4, 2, 0, 0, 0, 0, 0, 0, 0],
        [4, 2, 0, 0, 0, 0, 0, 0, 0],
        [4, 3, 0, 0, 0, 0, 0, 0, 0],
        [4, 3, 0, 0, 0, 0, 0, 0, 0],
        [4, 3, 2, 0, 0, 0, 0, 0, 0],
        [4, 3, 2, 0, 0, 0, 0, 0, 0],
        [4, 3, 3, 0, 0, 0, 0, 0, 0],
        [4, 3, 3, 0, 0, 0, 0, 0, 0],
        [4, 3, 3, 1, 0, 0, 0, 0, 0],
        [4, 3, 3, 1, 0, 0, 0, 0, 0],
        [4, 3, 3, 2, 0, 0, 0, 0, 0],
        [4, 3, 3, 2, 0, 0, 0, 0, 0],
        [4, 3, 3, 3, 1, 0, 0, 0, 0],
        [4, 3, 3, 3, 1, 0, 0, 0, 0],
        [4, 3, 3, 3, 2, 0, 0, 0, 0],
        [4, 3, 3, 3, 2, 0, 0, 0, 0],
      ],
    },
    subclassLevel: 3,
    subclassName: 'Sacred Oath',
    subclasses: [
      {
        id: 'oath-of-devotion',
        name: 'Oath of Devotion',
        description:
          'The Oath of Devotion binds a paladin to the loftiest ideals of justice, virtue, and order.',
        features: [
          {
            id: 'oath-spells-devotion',
            name: 'Oath Spells',
            level: 3,
            description:
              'You gain oath spells at the paladin levels listed: 3rd (protection from evil and good, sanctuary), 5th (lesser restoration, zone of truth), 9th (beacon of hope, dispel magic), 13th (freedom of movement, guardian of faith), 17th (commune, flame strike).',
          },
          {
            id: 'channel-divinity-devotion',
            name: 'Channel Divinity',
            level: 3,
            description:
              'You gain two Channel Divinity options: Sacred Weapon (imbue your weapon with positive energy, adding Charisma to attack rolls) and Turn the Unholy (force fiends and undead to flee).',
          },
          {
            id: 'aura-of-devotion',
            name: 'Aura of Devotion',
            level: 7,
            description:
              'You and friendly creatures within 10 feet of you can\'t be charmed while you are conscious. At 18th level, the range increases to 30 feet.',
          },
          {
            id: 'purity-of-spirit',
            name: 'Purity of Spirit',
            level: 15,
            description:
              'You are always under the effects of a protection from evil and good spell.',
          },
          {
            id: 'holy-nimbus',
            name: 'Holy Nimbus',
            level: 20,
            description:
              'As an action, you can emanate an aura of sunlight. For 1 minute, bright light shines from you in a 30-foot radius, and dim light shines 30 feet beyond that. Enemies that start their turn in the bright light take 10 radiant damage.',
          },
        ],
        source: 'srd-5.1',
      },
      {
        id: 'ignus-devotion',
        name: 'Oath of Devotion',
        description:
          "Ignus version: The Oath of Devotion binds a paladin to the loftiest ideals of justice, virtue, and order.",
        features: [],
        source: 'ignus',
      },
      {
        id: 'execution',
        name: 'Oath of Execution',
        description:
          "Paladins who swear the Oath of Execution are devoted to bringing justice through decisive action.",
        features: [],
        source: 'ignus',
      },
      {
        id: 'shadow',
        name: 'Oath of Shadow',
        description:
          "Those who take the Oath of Shadow find their home in the crevices of society, working as mercenaries and hired muscle.",
        features: [],
        source: 'ignus',
      },
    ],
    multiclassRequirements: [
      { ability: 'strength', minimum: 13 },
      { ability: 'charisma', minimum: 13 },
    ],
    multiclassProficiencies: {
      armor: ['light', 'medium', 'shield'],
      weapons: ['simple', 'martial'],
    },
    source: 'srd-5.1',
  },
  {
    id: 'ranger',
    name: 'Ranger',
    description:
      'A warrior who combats threats on the edges of civilization, using martial prowess and nature magic.',
    hitDie: 'd10',
    primaryAbility: ['dexterity', 'wisdom'],
    savingThrows: ['strength', 'dexterity'],
    armorProficiencies: ['light', 'medium', 'shield'],
    weaponProficiencies: ['simple', 'martial'],
    toolProficiencies: [],
    skillChoices: {
      count: 3,
      from: [
        'animal-handling',
        'athletics',
        'insight',
        'investigation',
        'nature',
        'perception',
        'stealth',
        'survival',
      ],
    },
    startingEquipment: [
      {
        choose: 1,
        from: [['scale-mail'], ['leather-armor']],
      },
      {
        choose: 1,
        from: [['shortsword', 'shortsword'], ['any-simple-melee', 'any-simple-melee']],
      },
      {
        choose: 1,
        from: [['dungeoneer-pack'], ['explorer-pack']],
      },
      'longbow',
      'quiver',
      'arrow',
      'arrow',
      'arrow',
      'arrow',
      'arrow',
      'arrow',
      'arrow',
      'arrow',
      'arrow',
      'arrow',
      'arrow',
      'arrow',
      'arrow',
      'arrow',
      'arrow',
      'arrow',
      'arrow',
      'arrow',
      'arrow',
      'arrow',
    ],
    features: [
      {
        id: 'favored-enemy',
        name: 'Favored Enemy',
        level: 1,
        description:
          'You have significant experience studying, tracking, hunting, and even talking to a certain type of enemy. Choose a type of favored enemy. You have advantage on Wisdom (Survival) checks to track your favored enemies, as well as on Intelligence checks to recall information about them.',
      },
      {
        id: 'natural-explorer',
        name: 'Natural Explorer',
        level: 1,
        description:
          'You are particularly familiar with one type of natural environment and are adept at traveling and surviving in such regions. When you make an Intelligence or Wisdom check related to your favored terrain, your proficiency bonus is doubled.',
      },
      {
        id: 'fighting-style-ranger',
        name: 'Fighting Style',
        level: 2,
        description:
          'You adopt a particular style of fighting as your specialty. Choose one of the following options: Archery, Defense, Dueling, or Two-Weapon Fighting.',
      },
      {
        id: 'spellcasting-ranger',
        name: 'Spellcasting',
        level: 2,
        description:
          'You have learned to use the magical essence of nature to cast spells. Wisdom is your spellcasting ability.',
      },
      {
        id: 'ranger-archetype',
        name: 'Ranger Archetype',
        level: 3,
        description:
          'You choose an archetype that you strive to emulate. Your choice grants you features at 3rd level and again at 7th, 11th, and 15th level.',
      },
      {
        id: 'primeval-awareness',
        name: 'Primeval Awareness',
        level: 3,
        description:
          'You can use your action and expend one ranger spell slot to focus your awareness on the region around you. For 1 minute per level of the spell slot you expend, you can sense whether certain creature types are present within 1 mile of you.',
      },
      {
        id: 'asi-ranger-4',
        name: 'Ability Score Improvement',
        level: 4,
        description:
          'You can increase one ability score by 2, or you can increase two ability scores by 1.',
      },
      {
        id: 'extra-attack-ranger',
        name: 'Extra Attack',
        level: 5,
        description:
          'You can attack twice, instead of once, whenever you take the Attack action on your turn.',
      },
      {
        id: 'favored-enemy-improvement',
        name: 'Favored Enemy Improvement',
        level: 6,
        description:
          'You choose one additional favored enemy, as well as an additional language.',
      },
      {
        id: 'natural-explorer-improvement',
        name: 'Natural Explorer Improvement',
        level: 6,
        description:
          'You choose one additional favored terrain.',
      },
      {
        id: 'asi-ranger-8',
        name: 'Ability Score Improvement',
        level: 8,
        description:
          'You can increase one ability score by 2, or you can increase two ability scores by 1.',
      },
      {
        id: 'lands-stride-ranger',
        name: "Land's Stride",
        level: 8,
        description:
          'Moving through nonmagical difficult terrain costs you no extra movement. You can also pass through nonmagical plants without being slowed by them.',
      },
      {
        id: 'hide-in-plain-sight',
        name: 'Hide in Plain Sight',
        level: 10,
        description:
          'You can spend 1 minute creating camouflage for yourself. You must have access to fresh mud, dirt, plants, soot, and other naturally occurring materials with which to create your camouflage.',
      },
      {
        id: 'natural-explorer-improvement-2',
        name: 'Natural Explorer Improvement',
        level: 10,
        description:
          'You choose one additional favored terrain.',
      },
      {
        id: 'asi-ranger-12',
        name: 'Ability Score Improvement',
        level: 12,
        description:
          'You can increase one ability score by 2, or you can increase two ability scores by 1.',
      },
      {
        id: 'vanish',
        name: 'Vanish',
        level: 14,
        description:
          'You can use the Hide action as a bonus action on your turn. Also, you can\'t be tracked by nonmagical means, unless you choose to leave a trail.',
      },
      {
        id: 'favored-enemy-improvement-2',
        name: 'Favored Enemy Improvement',
        level: 14,
        description:
          'You choose one additional favored enemy, as well as an additional language.',
      },
      {
        id: 'asi-ranger-16',
        name: 'Ability Score Improvement',
        level: 16,
        description:
          'You can increase one ability score by 2, or you can increase two ability scores by 1.',
      },
      {
        id: 'feral-senses',
        name: 'Feral Senses',
        level: 18,
        description:
          'You gain preternatural senses that help you fight creatures you can\'t see. When you attack a creature you can\'t see, your inability to see it doesn\'t impose disadvantage on your attack rolls against it.',
      },
      {
        id: 'asi-ranger-19',
        name: 'Ability Score Improvement',
        level: 19,
        description:
          'You can increase one ability score by 2, or you can increase two ability scores by 1.',
      },
      {
        id: 'foe-slayer',
        name: 'Foe Slayer',
        level: 20,
        description:
          'You become an unparalleled hunter of your enemies. Once on each of your turns, you can add your Wisdom modifier to the attack roll or the damage roll of an attack you make against one of your favored enemies.',
      },
    ],
    spellcasting: {
      ability: 'wisdom',
      type: 'half',
      ritual: false,
      spellsKnown: [0, 2, 3, 3, 4, 4, 5, 5, 6, 6, 7, 7, 8, 8, 9, 9, 10, 10, 11, 11],
      spellSlots: [
        [0, 0, 0, 0, 0, 0, 0, 0, 0],
        [2, 0, 0, 0, 0, 0, 0, 0, 0],
        [3, 0, 0, 0, 0, 0, 0, 0, 0],
        [3, 0, 0, 0, 0, 0, 0, 0, 0],
        [4, 2, 0, 0, 0, 0, 0, 0, 0],
        [4, 2, 0, 0, 0, 0, 0, 0, 0],
        [4, 3, 0, 0, 0, 0, 0, 0, 0],
        [4, 3, 0, 0, 0, 0, 0, 0, 0],
        [4, 3, 2, 0, 0, 0, 0, 0, 0],
        [4, 3, 2, 0, 0, 0, 0, 0, 0],
        [4, 3, 3, 0, 0, 0, 0, 0, 0],
        [4, 3, 3, 0, 0, 0, 0, 0, 0],
        [4, 3, 3, 1, 0, 0, 0, 0, 0],
        [4, 3, 3, 1, 0, 0, 0, 0, 0],
        [4, 3, 3, 2, 0, 0, 0, 0, 0],
        [4, 3, 3, 2, 0, 0, 0, 0, 0],
        [4, 3, 3, 3, 1, 0, 0, 0, 0],
        [4, 3, 3, 3, 1, 0, 0, 0, 0],
        [4, 3, 3, 3, 2, 0, 0, 0, 0],
        [4, 3, 3, 3, 2, 0, 0, 0, 0],
      ],
    },
    subclassLevel: 3,
    subclassName: 'Ranger Archetype',
    subclasses: [
      {
        id: 'hunter',
        name: 'Hunter',
        description:
          'Emulating the Hunter archetype means accepting your place as a bulwark between civilization and the terrors of the wilderness.',
        features: [
          {
            id: 'hunters-prey',
            name: "Hunter's Prey",
            level: 3,
            description:
              "Choose one of the following features: Colossus Slayer (deal an extra 1d8 damage to creatures below max HP), Giant Killer (reaction attack against Large+ creatures), or Horde Breaker (attack a second creature within 5 feet of original target).",
          },
          {
            id: 'defensive-tactics',
            name: 'Defensive Tactics',
            level: 7,
            description:
              'Choose one of the following features: Escape the Horde (opportunity attacks against you have disadvantage), Multiattack Defense (+4 AC after being hit), or Steel Will (advantage on saves against being frightened).',
          },
          {
            id: 'multiattack-hunter',
            name: 'Multiattack',
            level: 11,
            description:
              'Choose one of the following features: Volley (attack all creatures within 10 feet of a point in range) or Whirlwind Attack (melee attack against all creatures within 5 feet).',
          },
          {
            id: 'superior-hunters-defense',
            name: "Superior Hunter's Defense",
            level: 15,
            description:
              'Choose one of the following features: Evasion (no damage on successful Dex saves), Stand Against the Tide (redirect missed melee attack to another creature), or Uncanny Dodge (halve attack damage with reaction).',
          },
        ],
        source: 'srd-5.1',
      },
      {
        id: 'ignus-hunter',
        name: 'Hunter',
        description:
          "Ignus version: Emulating the Hunter archetype means accepting your place as a bulwark between civilization and the terrors of the wilderness.",
        features: [],
        source: 'ignus',
      },
      {
        id: 'trapper',
        name: 'Trapper',
        description:
          "Rangers who specialize as Trappers excel at using cunning devices and snares to control the battlefield.",
        features: [],
        source: 'ignus',
      },
      {
        id: 'tracker',
        name: 'Tracker',
        description:
          "Trackers are master hunters who can follow any trail and find any quarry.",
        features: [],
        source: 'ignus',
      },
    ],
    multiclassRequirements: [
      { ability: 'dexterity', minimum: 13 },
      { ability: 'wisdom', minimum: 13 },
    ],
    multiclassProficiencies: {
      armor: ['light', 'medium', 'shield'],
      weapons: ['simple', 'martial'],
      skills: { count: 1, from: ['animal-handling', 'athletics', 'insight', 'investigation', 'nature', 'perception', 'stealth', 'survival'] },
    },
    source: 'srd-5.1',
  },
  {
    id: 'sorcerer',
    name: 'Sorcerer',
    description:
      'A spellcaster who draws on inherent magic from a gift or bloodline, wielding raw magical power.',
    hitDie: 'd6',
    primaryAbility: ['charisma'],
    savingThrows: ['constitution', 'charisma'],
    armorProficiencies: [],
    weaponProficiencies: ['dagger', 'dart', 'sling', 'quarterstaff', 'light crossbow'],
    toolProficiencies: [],
    skillChoices: {
      count: 2,
      from: [
        'arcana',
        'deception',
        'insight',
        'intimidation',
        'persuasion',
        'religion',
      ],
    },
    startingEquipment: [
      {
        choose: 1,
        from: [['light-crossbow', 'bolt', 'bolt', 'bolt', 'bolt', 'bolt', 'bolt', 'bolt', 'bolt', 'bolt', 'bolt', 'bolt', 'bolt', 'bolt', 'bolt', 'bolt', 'bolt', 'bolt', 'bolt', 'bolt', 'bolt'], ['any-simple']],
      },
      {
        choose: 1,
        from: [['component-pouch'], ['arcane-focus']],
      },
      {
        choose: 1,
        from: [['dungeoneer-pack'], ['explorer-pack']],
      },
      'dagger',
      'dagger',
    ],
    features: [
      {
        id: 'spellcasting-sorcerer',
        name: 'Spellcasting',
        level: 1,
        description:
          'An event in your past, or in the life of a parent or ancestor, left an indelible mark on you, infusing you with arcane magic. Charisma is your spellcasting ability.',
      },
      {
        id: 'sorcerous-origin',
        name: 'Sorcerous Origin',
        level: 1,
        description:
          'Choose a sorcerous origin, which describes the source of your innate magical power. Your choice grants you features at 1st level and again at 6th, 14th, and 18th level.',
      },
      {
        id: 'font-of-magic',
        name: 'Font of Magic',
        level: 2,
        description:
          'You tap into a deep wellspring of magic within yourself. This wellspring is represented by sorcery points, which allow you to create a variety of magical effects. You have 2 sorcery points, and you gain more as you reach higher levels.',
      },
      {
        id: 'metamagic',
        name: 'Metamagic',
        level: 3,
        description:
          'You gain the ability to twist your spells to suit your needs. You gain two Metamagic options of your choice. You gain another one at 10th and 17th level.',
      },
      {
        id: 'asi-sorcerer-4',
        name: 'Ability Score Improvement',
        level: 4,
        description:
          'You can increase one ability score by 2, or you can increase two ability scores by 1.',
      },
      {
        id: 'asi-sorcerer-8',
        name: 'Ability Score Improvement',
        level: 8,
        description:
          'You can increase one ability score by 2, or you can increase two ability scores by 1.',
      },
      {
        id: 'asi-sorcerer-12',
        name: 'Ability Score Improvement',
        level: 12,
        description:
          'You can increase one ability score by 2, or you can increase two ability scores by 1.',
      },
      {
        id: 'asi-sorcerer-16',
        name: 'Ability Score Improvement',
        level: 16,
        description:
          'You can increase one ability score by 2, or you can increase two ability scores by 1.',
      },
      {
        id: 'asi-sorcerer-19',
        name: 'Ability Score Improvement',
        level: 19,
        description:
          'You can increase one ability score by 2, or you can increase two ability scores by 1.',
      },
      {
        id: 'sorcerous-restoration',
        name: 'Sorcerous Restoration',
        level: 20,
        description:
          'You regain 4 expended sorcery points whenever you finish a short rest.',
      },
    ],
    spellcasting: {
      ability: 'charisma',
      type: 'full',
      ritual: false,
      focus: 'arcane focus',
      cantripsKnown: [4, 4, 4, 5, 5, 5, 5, 5, 5, 6, 6, 6, 6, 6, 6, 6, 6, 6, 6, 6],
      spellsKnown: [2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 12, 13, 13, 14, 14, 15, 15, 15, 15],
      spellSlots: [
        [2, 0, 0, 0, 0, 0, 0, 0, 0],
        [3, 0, 0, 0, 0, 0, 0, 0, 0],
        [4, 2, 0, 0, 0, 0, 0, 0, 0],
        [4, 3, 0, 0, 0, 0, 0, 0, 0],
        [4, 3, 2, 0, 0, 0, 0, 0, 0],
        [4, 3, 3, 0, 0, 0, 0, 0, 0],
        [4, 3, 3, 1, 0, 0, 0, 0, 0],
        [4, 3, 3, 2, 0, 0, 0, 0, 0],
        [4, 3, 3, 3, 1, 0, 0, 0, 0],
        [4, 3, 3, 3, 2, 0, 0, 0, 0],
        [4, 3, 3, 3, 2, 1, 0, 0, 0],
        [4, 3, 3, 3, 2, 1, 0, 0, 0],
        [4, 3, 3, 3, 2, 1, 1, 0, 0],
        [4, 3, 3, 3, 2, 1, 1, 0, 0],
        [4, 3, 3, 3, 2, 1, 1, 1, 0],
        [4, 3, 3, 3, 2, 1, 1, 1, 0],
        [4, 3, 3, 3, 2, 1, 1, 1, 1],
        [4, 3, 3, 3, 3, 1, 1, 1, 1],
        [4, 3, 3, 3, 3, 2, 1, 1, 1],
        [4, 3, 3, 3, 3, 2, 2, 1, 1],
      ],
    },
    subclassLevel: 1,
    subclassName: 'Sorcerous Origin',
    subclasses: [
      {
        id: 'draconic-bloodline',
        name: 'Draconic Bloodline',
        description:
          'Your innate magic comes from draconic magic that was mingled with your blood or that of your ancestors.',
        features: [
          {
            id: 'dragon-ancestor',
            name: 'Dragon Ancestor',
            level: 1,
            description:
              'You choose one type of dragon as your ancestor. You can speak, read, and write Draconic. Additionally, whenever you make a Charisma check when interacting with dragons, your proficiency bonus is doubled.',
          },
          {
            id: 'draconic-resilience',
            name: 'Draconic Resilience',
            level: 1,
            description:
              'As magic flows through your body, it causes physical traits of your dragon ancestors to emerge. Your hit point maximum increases by 1 and increases by 1 again whenever you gain a level in this class. Additionally, when you aren\'t wearing armor, your AC equals 13 + your Dexterity modifier.',
          },
          {
            id: 'elemental-affinity',
            name: 'Elemental Affinity',
            level: 6,
            description:
              'When you cast a spell that deals damage of the type associated with your draconic ancestry, you can add your Charisma modifier to one damage roll of that spell. You can also spend 1 sorcery point to gain resistance to that damage type for 1 hour.',
          },
          {
            id: 'dragon-wings',
            name: 'Dragon Wings',
            level: 14,
            description:
              'You gain the ability to sprout a pair of dragon wings from your back, gaining a flying speed equal to your current speed. You can create these wings as a bonus action on your turn.',
          },
          {
            id: 'draconic-presence',
            name: 'Draconic Presence',
            level: 18,
            description:
              'You can channel the dread presence of your dragon ancestor, causing those around you to become awestruck or frightened. As an action, you can spend 5 sorcery points to draw on this power and exude an aura of awe or fear to a distance of 60 feet.',
          },
        ],
        source: 'srd-5.1',
      },
      {
        id: 'ignus-draconic',
        name: 'Draconic Bloodline',
        description:
          "Ignus version: Your innate magic comes from draconic magic mingled with your blood or that of your ancestors.",
        features: [],
        source: 'ignus',
      },
      {
        id: 'titanic',
        name: 'Titanic Bloodline',
        description:
          "At some point in your family's history, your bloodline mingled with that of a Titan.",
        features: [],
        source: 'ignus',
      },
      {
        id: 'kaltan',
        name: 'Kaltan Bloodline',
        description:
          "Descendants of the ancient Kaltash People, gifted with innate magic, grace, and charm.",
        features: [],
        source: 'ignus',
      },
    ],
    multiclassRequirements: [{ ability: 'charisma', minimum: 13 }],
    multiclassProficiencies: {
      armor: [],
      weapons: [],
    },
    source: 'srd-5.1',
  },
  {
    id: 'warlock',
    name: 'Warlock',
    description:
      'A wielder of magic that is derived from a bargain with an extraplanar entity.',
    hitDie: 'd8',
    primaryAbility: ['charisma'],
    savingThrows: ['wisdom', 'charisma'],
    armorProficiencies: ['light'],
    weaponProficiencies: ['simple'],
    toolProficiencies: [],
    skillChoices: {
      count: 2,
      from: [
        'arcana',
        'deception',
        'history',
        'intimidation',
        'investigation',
        'nature',
        'religion',
      ],
    },
    startingEquipment: [
      {
        choose: 1,
        from: [['light-crossbow', 'bolt', 'bolt', 'bolt', 'bolt', 'bolt', 'bolt', 'bolt', 'bolt', 'bolt', 'bolt', 'bolt', 'bolt', 'bolt', 'bolt', 'bolt', 'bolt', 'bolt', 'bolt', 'bolt', 'bolt'], ['any-simple']],
      },
      {
        choose: 1,
        from: [['component-pouch'], ['arcane-focus']],
      },
      {
        choose: 1,
        from: [['scholar-pack'], ['dungeoneer-pack']],
      },
      'leather-armor',
      'any-simple',
      'dagger',
      'dagger',
    ],
    features: [
      {
        id: 'otherworldly-patron',
        name: 'Otherworldly Patron',
        level: 1,
        description:
          'You have struck a bargain with an otherworldly being of your choice. Your choice grants you features at 1st level and again at 6th, 10th, and 14th level.',
      },
      {
        id: 'pact-magic',
        name: 'Pact Magic',
        level: 1,
        description:
          'Your arcane research and the magic bestowed on you by your patron have given you facility with spells. You have a limited number of spell slots that recharge on a short or long rest. Charisma is your spellcasting ability.',
      },
      {
        id: 'eldritch-invocations',
        name: 'Eldritch Invocations',
        level: 2,
        description:
          'In your study of occult lore, you have unearthed eldritch invocations, fragments of forbidden knowledge that imbue you with an abiding magical ability. You gain two eldritch invocations of your choice.',
      },
      {
        id: 'pact-boon',
        name: 'Pact Boon',
        level: 3,
        description:
          'Your otherworldly patron bestows a gift upon you for your loyal service. You gain one of the following features of your choice: Pact of the Chain, Pact of the Blade, or Pact of the Tome.',
      },
      {
        id: 'asi-warlock-4',
        name: 'Ability Score Improvement',
        level: 4,
        description:
          'You can increase one ability score by 2, or you can increase two ability scores by 1.',
      },
      {
        id: 'asi-warlock-8',
        name: 'Ability Score Improvement',
        level: 8,
        description:
          'You can increase one ability score by 2, or you can increase two ability scores by 1.',
      },
      {
        id: 'mystic-arcanum-6',
        name: 'Mystic Arcanum (6th level)',
        level: 11,
        description:
          'Your patron bestows upon you a magical secret called an arcanum. Choose one 6th-level spell from the warlock spell list as this arcanum. You can cast your arcanum spell once without expending a spell slot.',
      },
      {
        id: 'asi-warlock-12',
        name: 'Ability Score Improvement',
        level: 12,
        description:
          'You can increase one ability score by 2, or you can increase two ability scores by 1.',
      },
      {
        id: 'mystic-arcanum-7',
        name: 'Mystic Arcanum (7th level)',
        level: 13,
        description:
          'Choose one 7th-level spell from the warlock spell list as this arcanum.',
      },
      {
        id: 'mystic-arcanum-8',
        name: 'Mystic Arcanum (8th level)',
        level: 15,
        description:
          'Choose one 8th-level spell from the warlock spell list as this arcanum.',
      },
      {
        id: 'asi-warlock-16',
        name: 'Ability Score Improvement',
        level: 16,
        description:
          'You can increase one ability score by 2, or you can increase two ability scores by 1.',
      },
      {
        id: 'mystic-arcanum-9',
        name: 'Mystic Arcanum (9th level)',
        level: 17,
        description:
          'Choose one 9th-level spell from the warlock spell list as this arcanum.',
      },
      {
        id: 'asi-warlock-19',
        name: 'Ability Score Improvement',
        level: 19,
        description:
          'You can increase one ability score by 2, or you can increase two ability scores by 1.',
      },
      {
        id: 'eldritch-master',
        name: 'Eldritch Master',
        level: 20,
        description:
          'You can draw on your inner reserve of mystical power while entreating your patron to regain expended spell slots. You can spend 1 minute entreating your patron for aid to regain all your expended spell slots from your Pact Magic feature.',
      },
    ],
    spellcasting: {
      ability: 'charisma',
      type: 'pact',
      ritual: false,
      focus: 'arcane focus',
      cantripsKnown: [2, 2, 2, 3, 3, 3, 3, 3, 3, 4, 4, 4, 4, 4, 4, 4, 4, 4, 4, 4],
      spellsKnown: [2, 3, 4, 5, 6, 7, 8, 9, 10, 10, 11, 11, 12, 12, 13, 13, 14, 14, 15, 15],
      spellSlots: [
        [1, 0, 0, 0, 0, 0, 0, 0, 0],
        [2, 0, 0, 0, 0, 0, 0, 0, 0],
        [0, 2, 0, 0, 0, 0, 0, 0, 0],
        [0, 2, 0, 0, 0, 0, 0, 0, 0],
        [0, 0, 2, 0, 0, 0, 0, 0, 0],
        [0, 0, 2, 0, 0, 0, 0, 0, 0],
        [0, 0, 0, 2, 0, 0, 0, 0, 0],
        [0, 0, 0, 2, 0, 0, 0, 0, 0],
        [0, 0, 0, 0, 2, 0, 0, 0, 0],
        [0, 0, 0, 0, 2, 0, 0, 0, 0],
        [0, 0, 0, 0, 3, 0, 0, 0, 0],
        [0, 0, 0, 0, 3, 0, 0, 0, 0],
        [0, 0, 0, 0, 3, 0, 0, 0, 0],
        [0, 0, 0, 0, 3, 0, 0, 0, 0],
        [0, 0, 0, 0, 3, 0, 0, 0, 0],
        [0, 0, 0, 0, 3, 0, 0, 0, 0],
        [0, 0, 0, 0, 4, 0, 0, 0, 0],
        [0, 0, 0, 0, 4, 0, 0, 0, 0],
        [0, 0, 0, 0, 4, 0, 0, 0, 0],
        [0, 0, 0, 0, 4, 0, 0, 0, 0],
      ],
    },
    subclassLevel: 1,
    subclassName: 'Otherworldly Patron',
    subclasses: [
      {
        id: 'the-fiend',
        name: 'The Fiend',
        description:
          'You have made a pact with a fiend from the lower planes of existence, a being whose aims are evil, even if you strive against those aims.',
        features: [
          {
            id: 'dark-ones-blessing',
            name: "Dark One's Blessing",
            level: 1,
            description:
              'When you reduce a hostile creature to 0 hit points, you gain temporary hit points equal to your Charisma modifier + your warlock level (minimum of 1).',
          },
          {
            id: 'dark-ones-own-luck',
            name: "Dark One's Own Luck",
            level: 6,
            description:
              'You can call on your patron to alter fate in your favor. When you make an ability check or a saving throw, you can use this feature to add a d10 to your roll. You can do so after seeing the initial roll but before any of the roll\'s effects occur. Once you use this feature, you can\'t use it again until you finish a short or long rest.',
          },
          {
            id: 'fiendish-resilience',
            name: 'Fiendish Resilience',
            level: 10,
            description:
              'You can choose one damage type when you finish a short or long rest. You gain resistance to that damage type until you choose a different one with this feature.',
          },
          {
            id: 'hurl-through-hell',
            name: 'Hurl Through Hell',
            level: 14,
            description:
              'When you hit a creature with an attack, you can use this feature to instantly transport the target through the lower planes. The creature disappears and hurtles through a nightmare landscape. At the end of your next turn, the target returns to the space it previously occupied, or the nearest unoccupied space. If the target is not a fiend, it takes 10d10 psychic damage as it reels from its horrific experience.',
          },
        ],
        source: 'srd-5.1',
      },
      {
        id: 'ignus-fiend',
        name: 'The Fiend',
        description:
          "Ignus version: You have made a pact with a fiend from the lower planes of existence.",
        features: [],
        source: 'ignus',
      },
      {
        id: 'dragon',
        name: 'The Dragon',
        description:
          "You have made a pact with a powerful dragon, gaining draconic powers.",
        features: [],
        source: 'ignus',
      },
      {
        id: 'outsider',
        name: 'The Outsider',
        description:
          "You have made a pact with an otherworldly entity from beyond known planes.",
        features: [],
        source: 'ignus',
      },
    ],
    multiclassRequirements: [{ ability: 'charisma', minimum: 13 }],
    multiclassProficiencies: {
      armor: ['light'],
      weapons: ['simple'],
    },
    source: 'srd-5.1',
  },
];

/**
 * Validate all class data at module load time
 * This ensures data integrity and catches errors early in development
 */
function validateClasses(data: CharacterClass[]): CharacterClass[] {
  const ClassArraySchema = z.array(ClassSchema);
  const result = ClassArraySchema.safeParse(data);

  if (!result.success) {
    const formatted = JSON.stringify(result.error.format(), null, 2);
    throw new Error(`Class data validation failed:\n${formatted}`);
  }

  return data;
}

export const classes: CharacterClass[] = validateClasses(rawClasses);
